import { useEffect } from 'react'

// Publishes the bottom nav's ACTUAL height as `--nav-h` on :root, so nothing has to
// guess it.
//
// Why this exists: the nav is `minHeight:60px` plus
// `paddingBottom: max(8px, var(--chrome-bottom))`, and --chrome-bottom is measured from
// visualViewport to clear iOS Safari's floating chrome. Its height is therefore DYNAMIC
// and device-dependent — it measured 95px in a 1280x800 desktop viewport. Call sites had
// been hardcoding it, and had drifted to FOUR different numbers (72, 76, 90, 170), so:
//   • `bottom: calc(72px + …)` overlays sat 23px UP INSIDE the nav (the update prompt,
//     rating toast, Episode Pulse, review invite, ShowDetail's sticky bar)
//   • `paddingBottom: calc(72px + …)` pages hid their last rows behind it
// which is what Jason saw as "the bottom menu cutting off objects" (2026-07-29).
//
// IMPORTANT for call sites: the published value ALREADY INCLUDES the nav's bottom padding
// and therefore the safe-area inset. Use `calc(var(--nav-h) + 12px)` and do NOT add
// `env(safe-area-inset-bottom)` again — that was the double-count this replaced.
//
// Removed on unmount so a nav-less surface (auth gate, full-screen room) doesn't leave a
// stale reservation behind.
export function useNavHeight(ref) {
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const root = document.documentElement
    const publish = () => {
      const h = el.getBoundingClientRect().height
      if (h > 0) root.style.setProperty('--nav-h', `${Math.round(h)}px`)
    }
    publish()
    // ResizeObserver rather than a resize listener: the height also changes when
    // --chrome-bottom moves (iOS chrome collapsing on scroll), which fires no resize.
    let ro
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(publish)
      ro.observe(el)
    }
    window.addEventListener('orientationchange', publish)
    return () => {
      ro?.disconnect()
      window.removeEventListener('orientationchange', publish)
      root.style.removeProperty('--nav-h')
    }
  }, [ref])
}
